import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.#handleVariantUpdate));
    this.addEventListener(ThemeEvents.variantSelected, /** @type {EventListener} */ (this.#handleVariantSelected));
    this.addEventListener(SlideshowSelectEvent.eventName, /** @type {EventListener} */ (this.#handleSlideshowSelect));
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // ATC bar click handler
    this.#initAtcBar();

    // Size selector click handler
    this.#initSizeSelector();

    // Gallery progress bar (mobile)
    this.#initProgressBar();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#atcBarAbortController?.abort();
    this.#sizeSelectorAbortController?.abort();
    // Drop any pending cart-settle listeners / timers for an in-flight add.
    this.#onCartSettled();
  }

  /**
   * Re-initializes the card after its children have been wholesale replaced
   * (e.g. a product-card-group-selector colour swap). The <product-card>
   * element itself is preserved across the swap, so connectedCallback never
   * re-runs — its `refs` stay cached against the old children and its size/ATC
   * click listeners stay bound to now-detached nodes. Without this, a swapped
   * card's size button has no handler and reads a stale quick-add ref, so it
   * never adds to cart. We refresh refs synchronously, tear down the old
   * listeners, and rebind every handler against the new DOM. The delegated
   * `click`/variant listeners on <product-card> itself survive the swap, so we
   * deliberately don't re-add them here (that would double-fire).
   */
  reinitAfterSwap() {
    // Synchronously rebuild refs against the new children (no idle wait).
    this.updatedCallback();

    // Tear down listeners bound to the now-detached old children.
    this.#atcBarAbortController?.abort();
    this.#sizeSelectorAbortController?.abort();

    // Rebind against the fresh children.
    this.#handleQuickAdd();
    this.#initAtcBar();
    this.#initSizeSelector();
    this.#initProgressBar();
  }

  /** @type {AbortController | null} */
  #atcBarAbortController = null;

  /** @type {AbortController | null} */
  #sizeSelectorAbortController = null;

  /**
   * Initialises click handlers for all ATC bar buttons (desktop bar + mobile
   * cart icon). Sets quick-add to "add" mode and clicks the submit button,
   * reusing the theme's standard add-to-cart flow (product-form → fetch →
   * CartAddEvent → 3rd-party cart app).
   */
  #initAtcBar() {
    const atcBars = this.querySelectorAll('[data-atc-bar]');
    if (!atcBars.length) return;

    this.#atcBarAbortController = new AbortController();

    for (const atcBar of atcBars) {
      atcBar.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.#submitQuickAddForm(/** @type {HTMLElement} */ (atcBar));
        },
        { signal: this.#atcBarAbortController.signal }
      );
    }
  }

  /**
   * Initialises click handlers on the size selector buttons.
   * Clicking a size sets the variant on the quick-add form and submits it,
   * reusing the theme's standard add-to-cart flow (product-form → fetchConfig
   * → CartAddEvent) so that 3rd-party cart apps are fully supported.
   */
  #initSizeSelector() {
    const sizeSelector = this.querySelector('[data-size-selector]');
    if (!sizeSelector) return;

    this.#sizeSelectorAbortController = new AbortController();

    sizeSelector.addEventListener(
      'click',
      (event) => {
        const btn = /** @type {HTMLButtonElement} */ (event.target)?.closest('.product-card-gallery__size-btn');
        if (!btn || btn.disabled) return;

        event.preventDefault();
        event.stopPropagation();

        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        // Set the variant on the quick-add form and submit
        const quickAdd = this.refs.quickAdd;
        if (!quickAdd) return;

        const variantInput = quickAdd.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
          variantInput.disabled = false;
        }

        this.#submitQuickAddForm(btn);
      },
      { signal: this.#sizeSelectorAbortController.signal }
    );
  }

  /** Guards against overlapping submissions (double-clicks / spam). */
  #isSubmitting = false;

  /** @type {HTMLElement | undefined} The control currently showing a loading state. */
  #loadingTrigger;

  /**
   * Adds the selected variant to the cart by triggering the quick-add form's
   * real add-to-cart button — the exact same path the PDP uses. This is the
   * key to performance: when MonsterCart is loaded it intercepts the native
   * form submit and runs its own single optimised add (and opens its drawer),
   * so there is no extra round-trip. When MonsterCart is absent the theme's
   * product-form-component handles the submit. Either way it's one request,
   * matching the instant PDP behaviour.
   *
   * The loading state is cleared by the cart-update event (#onCartSettled),
   * which fires once the add — ours or MonsterCart's — has settled.
   *
   * @param {HTMLElement} [trigger] - The control the user interacted with
   *   (ATC bar or a size button), used to show the loading state.
   */
  #submitQuickAddForm(trigger) {
    if (this.#isSubmitting) return;

    const quickAdd = this.refs.quickAdd;
    if (!quickAdd) return;

    quickAdd.setAttribute('data-quick-add-button', 'add');

    const form = quickAdd.querySelector('form[data-type="add-to-cart-form"]');
    const addButton = /** @type {HTMLButtonElement | null} */ (
      form?.querySelector('add-to-cart-component button[type="submit"]')
    );
    // No real add button (e.g. multi-variant "choose options" card) or the
    // variant is unavailable: bail before showing a spinner that would never
    // clear (a disabled button click is a no-op, so no cart event fires).
    if (!addButton || addButton.disabled) return;

    this.#isSubmitting = true;
    this.#setLoading(trigger, true);

    // Listen once for the cart event that this add will produce, so the loading
    // state is cleared when the cart settles — whether the add was handled by
    // MonsterCart or the theme's product-form-component. cart:error covers the
    // failure path (e.g. sold out) so the spinner clears on errors too.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartSettled, { once: true });
    document.addEventListener(ThemeEvents.cartError, this.#onCartSettled, { once: true });
    if (window.monster_upsells) {
      document.addEventListener('cart:updated', this.#onCartSettled, { once: true });
    }

    // Safety net: if no cart event ever arrives, clear the spinner so it never
    // hangs. Uses a generous delay so genuinely slow adds still clear via their
    // own event (clearing too early would let a second click double-add while
    // the first request is still in flight).
    this.#loadingTimeout = setTimeout(() => this.#onCartSettled(), 10000);

    // Trigger the real add-to-cart button — same entry point as the PDP.
    addButton.click();
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #loadingTimeout;

  /** Clears the loading/submitting state once the cart has settled. */
  #onCartSettled = () => {
    clearTimeout(this.#loadingTimeout);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartSettled);
    document.removeEventListener(ThemeEvents.cartError, this.#onCartSettled);
    document.removeEventListener('cart:updated', this.#onCartSettled);
    this.#isSubmitting = false;
    this.#setLoading(this.#loadingTrigger, false);
  };

  /**
   * Toggles the loading state on the triggering control while an add-to-cart
   * request is in flight. Uses `aria-busy` (announced to assistive tech) plus
   * an `is-loading` class for the spinner, and disables the control to prevent
   * duplicate submissions.
   *
   * @param {HTMLElement} [trigger] - The control that started the request.
   * @param {boolean} loading - Whether the request is in flight.
   */
  #setLoading(trigger, loading) {
    if (!trigger) return;

    this.#loadingTrigger = loading ? trigger : undefined;

    trigger.classList.toggle('is-loading', loading);
    trigger.setAttribute('aria-busy', String(loading));

    if (trigger instanceof HTMLButtonElement) {
      trigger.disabled = loading;
    }
  }

  /**
   * Initialises the gallery progress bar by calculating the thumb width
   * based on the total number of slides.
   */
  #initProgressBar() {
    const progress = this.refs.galleryProgress;
    if (!progress) return;

    const slideCount = parseInt(progress.dataset.slideCount || '0', 10);
    if (slideCount <= 1) return;

    const thumbWidth = 100 / slideCount;
    progress.style.setProperty('--progress-thumb-width', `${thumbWidth}%`);
    progress.style.setProperty('--progress-thumb-x', '0px');
  }

  /**
   * Updates the progress bar thumb position based on the current slide index.
   * @param {number} index - The current slide index.
   */
  #updateProgressBar(index) {
    const progress = this.refs.galleryProgress;
    if (!progress) return;

    // Use visible (non-hidden) slides for accurate count
    const { slideshow } = this.refs;
    const visibleSlides = slideshow?.slides?.filter((s) => !s.hidden) || [];
    const slideCount = visibleSlides.length;
    if (slideCount <= 1) return;

    // Clamp index to valid range
    const clampedIndex = Math.max(0, Math.min(index, slideCount - 1));
    const trackWidth = progress.offsetWidth;
    const thumbWidth = trackWidth / slideCount;
    const translateX = clampedIndex * thumbWidth;

    progress.style.setProperty('--progress-thumb-width', `${(100 / slideCount)}%`);
    progress.style.setProperty('--progress-thumb-x', `${translateX}px`);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Whether the results grid this card sits in has the second-image view active.
   * @returns {boolean}
   */
  get #isSecondImageViewActive() {
    return this.closest('[product-grid-second-image]') !== null;
  }

  /**
   * Snaps the gallery back to a resting slide when the grid-wide second-image
   * view toggles. The second image itself is promoted purely with CSS order
   * (keyed off the grid's [product-grid-second-image] attribute), so all this
   * needs to do is park the scroller: on the first slide when enabling, and
   * on the selected variant's image (or the first slide) when disabling.
   *
   * @param {boolean} enabled
   */
  resetGalleryForSecondImageView(enabled) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.resetVariant.cancel();

    if (!enabled && this.variantPicker) {
      this.#resetVariant();
      return;
    }

    // Not select(0): the slideshow usually already reports slide 0 as current
    // (so select would no-op), while snap-target preservation moves the
    // scroller to follow the reordered first slide — park it directly.
    slideshow.refs.scroller?.scrollTo({ left: 0, behavior: 'instant' });
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
    this.#updateProgressBar(event.detail.index);
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    // The second image is already showing grid-wide — no hover swap.
    if (this.#isSecondImageViewActive) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the first slide or the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    // The second image is already showing grid-wide — no hover swap.
    if (this.#isSecondImageViewActive) return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      // Always go back to the first slide (index 0) on hover out
      slideshow.select(0, undefined, { animate: false });
      this.#previousSlideIndex = null;
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
